/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Tag, Button, Space } from "antd";
import { Link, useNavigate } from 'react-router-dom';
import { imagingOrderService, imagingStudyService, imagingDepartmentService } from '../../../services/imagingService';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { formatDate } from '../../../lib/format';

const ImagingOrderList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [studies, setStudies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const fetchStudies = useCallback(async () => {
    try {
      const response = await imagingStudyService.getAll({ per_page: 100, is_active: true });
      setStudies(response.data || []);
    } catch (error) {
      console.error('Error fetching studies:', error);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await imagingDepartmentService.getAll({ per_page: 100, is_active: true });
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  const fetchOrders = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await imagingOrderService.getAll({
        per_page: pageSize,
        status: statusFilter || undefined,
        page
      });

      setOrders((response.data || []).map(o => ({
        id: o.id,
        patient_name: o.patient ? `${o.patient.first_name} ${o.patient.last_name}` : 'N/A',
        study_name: o.study?.name || 'N/A',
        department_name: o.study?.department?.name || 'N/A',
        doctor_name: o.doctor ? `${o.doctor.first_name} ${o.doctor.last_name}` : 'N/A',
        clinical_indication: o.clinical_indication,
        status: o.status,
        ordered_at: o.ordered_at,
        completed_at: o.completed_at,
        has_result: !!o.result,
      })));
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Error al cargar órdenes de imagen');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchStudies();
    fetchDepartments();
  }, [fetchStudies, fetchDepartments]);

  useEffect(() => {
    fetchOrders(pagination.current, pagination.pageSize);
  }, [fetchOrders, pagination.current, pagination.pageSize]);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const getStatusTag = (status) => {
    const colors = {
      pending: 'orange',
      in_progress: 'blue',
      completed: 'green',
      cancelled: 'red'
    };
    const labels = {
      pending: 'Pendiente',
      in_progress: 'En Proceso',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return <Tag color={colors[status]}>{labels[status]}</Tag>;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 60,
    },
    {
      title: "Paciente",
      dataIndex: "patient_name",
      sorter: (a, b) => a.patient_name.localeCompare(b.patient_name),
    },
    {
      title: "Estudio",
      dataIndex: "study_name",
    },
    {
      title: "Departamento",
      dataIndex: "department_name",
    },
    {
      title: "Médico Solicitante",
      dataIndex: "doctor_name",
    },
    {
      title: "Fecha",
      dataIndex: "ordered_at",
      render: (date) => date ? formatDate(date) : 'N/A',
      sorter: (a, b) => new Date(a.ordered_at) - new Date(b.ordered_at),
    },
    {
      title: "Estado",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Resultado",
      dataIndex: "has_result",
      render: (hasResult) => (
        hasResult ? <Tag color="green">Con Resultado</Tag> : <Tag color="default">Sin Resultado</Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/imaging-order/${record.id}`)}
          >
            Ver
          </Button>
        </Space>
      ),
    }
  ];

  return (
    <>
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Órdenes de Imagen</h3>
              <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Órdenes de Imagen' }]} />
            </div>
            <div className="col-auto text-end float-end ms-auto">
              <Link to="/imaging/new" className="btn btn-primary">
                <PlusOutlined className="me-2" />
                Nueva Orden
              </Link>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title">Lista de Órdenes</h5>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={statusFilter || ''}
                      onChange={(e) => {
                        setStatusFilter(e.target.value || null);
                        setPagination({ ...pagination, current: 1 });
                      }}
                    >
                      <option value="">Todos los estados</option>
                      <option value="pending">Pendiente</option>
                      <option value="in_progress">En Proceso</option>
                      <option value="completed">Completado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>
                </div>

                <Table
                  columns={columns}
                  dataSource={orders}
                  loading={loading}
                  rowKey="id"
                  pagination={{
                    ...pagination,
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} registros`
                  }}
                  onChange={handleTableChange}
                />
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default ImagingOrderList;
